import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link, useSearchParams } from 'react-router-dom';

import * as legalApi from '../api/legal';
import type { LegalDocument, LegalKind } from '../api/legal';
import { LEGAL_KIND_LABEL, LEGAL_KIND_PATH } from '../api/legal';
import { LegalMarkdown } from '../components/legal/LegalMarkdown';

function formatDate(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';
}

/**
 * Public `/terms` and `/privacy` (LEGAL-TERMS): the version in force, a note
 * when a new one is scheduled, and any published version by `?version=n`.
 * No sign-in needed — the join forms link here in a new tab.
 */
export function LegalPage({ kind }: { kind: LegalKind }) {
  const [params] = useSearchParams();
  const versionParam = Number(params.get('version')) || null;
  const [doc, setDoc] = useState<LegalDocument | null>(null);
  const [upcoming, setUpcoming] = useState<LegalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const load = versionParam
      ? legalApi.getLegalVersion(kind, versionParam).then((d) => ({
          current: d,
          upcoming: null,
        }))
      : legalApi.getLegalKind(kind);
    load
      .then((r) => {
        if (cancelled) return;
        setDoc(r.current);
        setUpcoming(r.upcoming);
      })
      .catch(() => {
        if (!cancelled) setError('This document could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, versionParam]);

  const label = LEGAL_KIND_LABEL[kind];
  const path = LEGAL_KIND_PATH[kind];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Stack spacing={3}>
        {loading ? (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : doc === null ? (
          <>
            <Typography variant="h4">{label}</Typography>
            <Typography color="text.secondary">
              The {label} hasn't been published yet.
            </Typography>
          </>
        ) : (
          <>
            {versionParam && (
              <Alert severity="info">
                You're reading version {doc.version}, in effect from{' '}
                {formatDate(doc.effective_at)}.{' '}
                <MuiLink component={Link} to={path}>
                  Read the current version
                </MuiLink>
              </Alert>
            )}
            {upcoming && (
              <Alert severity="info">
                An updated {label} takes effect on{' '}
                {formatDate(upcoming.effective_at)}.{' '}
                <MuiLink component={Link} to={`${path}?version=${upcoming.version}`}>
                  Read the new version
                </MuiLink>
              </Alert>
            )}
            <Stack spacing={0.5}>
              <Typography variant="h3" component="h1">
                {doc.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Version {doc.version} · Effective {formatDate(doc.effective_at)}
              </Typography>
            </Stack>
            <LegalMarkdown source={doc.body_md} />
          </>
        )}
        <Typography variant="body2" color="text.secondary">
          <MuiLink component={Link} to="/terms">
            Terms of Use
          </MuiLink>
          {' · '}
          <MuiLink component={Link} to="/privacy">
            Privacy Policy
          </MuiLink>
          {' · '}
          <MuiLink component={Link} to="/login">
            Sign in
          </MuiLink>
        </Typography>
      </Stack>
    </Container>
  );
}
